import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { DefaultsRow, DefaultsUpdate, DefaultsUpdateResponse } from '@lms/shared-types'
import { Modal } from '../../primitives/Modal'
import { ApiError } from '../../../api/client'
import { getDefaults, updateDefault } from '../../../api/leave-credits'

// Defaults grid = the 3 employment-cohort × 10 leave-type matrix HR uses to
// set base entitlements. Each cell save propagates to every employee whose
// (employment_type, on_probation) matches — except those carrying an override
// (per-employee adjustments are preserved by the propagation logic).

type LeaveType = DefaultsRow['leaveType']

interface CellId {
  leaveType: LeaveType
  col: 'fullTimeProbation' | 'fullTimeConfirmed' | 'contractual'
}

type Col = CellId['col']

const LEAVE_TYPE_ORDER: ReadonlyArray<LeaveType> = [
  'annual',
  'medical',
  'hospitalization',
  'maternity',
  'paternity',
  'birthday',
  'compassionate',
  'marriage',
  'replacement',
  // Unpaid is intentionally omitted — it is uncapped leave with no cohort
  // entitlement (the grid rows were dropped in migration 0200), so there is
  // nothing to display or edit here.
]

const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  annual: 'Annual',
  medical: 'Medical',
  hospitalization: 'Hospitalization',
  maternity: 'Maternity',
  paternity: 'Paternity',
  birthday: 'Birthday',
  compassionate: 'Compassionate',
  marriage: 'Marriage',
  replacement: 'Replacement',
  unpaid: 'Unpaid',
}

const COLUMNS: ReadonlyArray<{ col: Col; label: string; hint: string }> = [
  { col: 'fullTimeProbation', label: 'FT · Probation', hint: 'Full-time on probation' },
  { col: 'fullTimeConfirmed', label: 'FT · Confirmed', hint: 'Full-time confirmed' },
  { col: 'contractual', label: 'Contractual', hint: 'Contractual employees' },
]

function cellKey(id: CellId): string {
  return `${id.leaveType}:${id.col}`
}

function colToUpdate(col: Col): Pick<DefaultsUpdate, 'employmentType' | 'onProbation'> {
  if (col === 'fullTimeProbation') return { employmentType: 'full_time', onProbation: true }
  if (col === 'fullTimeConfirmed') return { employmentType: 'full_time', onProbation: false }
  return { employmentType: 'contractual', onProbation: false }
}

function applyUpdate(rows: DefaultsRow[], leaveType: LeaveType, col: Col, days: number): DefaultsRow[] {
  return rows.map((row) => (row.leaveType === leaveType ? { ...row, [col]: days } : row))
}

interface DefaultsModalProps {
  open: boolean
  onClose: () => void
  /** Fires after each successful save so the parent can refresh dependent
   *  views (e.g. the per-employee balance roster). */
  onSaved?: (response: DefaultsUpdateResponse) => void
}

export function DefaultsModal({ open, onClose, onSaved }: DefaultsModalProps): JSX.Element {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState<CellId | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [lastPropagated, setLastPropagated] = useState<DefaultsUpdateResponse | null>(null)

  const defaultsQuery = useQuery({
    queryKey: ['leave-credits-defaults'],
    queryFn: getDefaults,
    enabled: open,
  })

  const saveMutation = useMutation({
    mutationFn: (input: DefaultsUpdate) => updateDefault(input),
    onSuccess: (response: DefaultsUpdateResponse) => {
      setError(null)
      setLastPropagated(response)
      setEditing(null)
      queryClient.setQueryData<DefaultsRow[]>(['leave-credits-defaults'], (prev) => {
        if (!prev) return prev
        const col: Col = response.onProbation
          ? 'fullTimeProbation'
          : response.employmentType === 'full_time'
            ? 'fullTimeConfirmed'
            : 'contractual'
        return applyUpdate(prev, response.leaveType, col, response.days)
      })
      onSaved?.(response)
    },
    onError: (err) => {
      const status = err instanceof ApiError ? err.status : 500
      const msg = err instanceof Error ? err.message : 'Save failed'
      setError(`${msg} (${status})`)
      setEditing(null)
    },
  })

  const handleCommit = (id: CellId, days: number): void => {
    const { employmentType, onProbation } = colToUpdate(id.col)
    saveMutation.mutate({ leaveType: id.leaveType, employmentType, onProbation, days })
  }

  const handleClose = (): void => {
    setEditing(null)
    setError(null)
    setLastPropagated(null)
    onClose()
  }

  const rows = defaultsQuery.data ?? []

  return (
    <Modal open={open} onClose={handleClose} title="Default leave credits" maxWidth={640}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
          Base entitlements per employment cohort. Edits apply to every matching employee
          who doesn&apos;t already have an individual adjustment. Click a cell to edit;
          press Enter or click away to save.
        </p>

        {error ? (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              background: 'var(--danger-bg)',
              color: 'var(--danger-fg)',
              border: '1px solid var(--danger-line)',
              borderRadius: 8,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : null}

        {lastPropagated ? (
          <div
            data-testid="defaults-propagation-chip"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--success-bg)',
              color: 'var(--success-fg)',
              border: '1px solid var(--success-line)',
              fontSize: 12,
              fontWeight: 600,
              alignSelf: 'flex-start',
            }}
          >
            Saved {LEAVE_TYPE_LABEL[lastPropagated.leaveType]} · propagated to{' '}
            {lastPropagated.touchedBalances}{' '}
            {lastPropagated.touchedBalances === 1 ? 'balance' : 'balances'}
          </div>
        ) : null}

        {defaultsQuery.isLoading ? (
          <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-3)', fontSize: 13 }}>
            Loading…
          </div>
        ) : defaultsQuery.isError ? (
          <div role="alert" style={{ padding: 16, color: 'var(--danger-fg)', fontSize: 13 }}>
            Failed to load defaults.
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table
              data-testid="defaults-modal-grid"
              style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px 8px 0',
                      color: 'var(--text-2)',
                      fontWeight: 600,
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    Leave type
                  </th>
                  {COLUMNS.map(({ col, label, hint }) => (
                    <th
                      key={col}
                      title={hint}
                      style={{
                        textAlign: 'center',
                        padding: '8px',
                        color: 'var(--text-2)',
                        fontWeight: 600,
                        borderBottom: '1px solid var(--border)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {LEAVE_TYPE_ORDER.map((leaveType) => {
                  const row = rows.find((r) => r.leaveType === leaveType)
                  if (!row) return null
                  return (
                    <tr
                      key={leaveType}
                      data-testid={`defaults-modal-row-${leaveType}`}
                      style={{ borderBottom: '1px solid var(--french-gray-2, var(--border))' }}
                    >
                      <td
                        style={{
                          padding: '8px 12px 8px 0',
                          color: 'var(--text)',
                          fontWeight: 500,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {LEAVE_TYPE_LABEL[leaveType]}
                      </td>
                      {COLUMNS.map(({ col }) => {
                        const id: CellId = { leaveType, col }
                        const isEditing = editing !== null && cellKey(editing) === cellKey(id)
                        return (
                          <td key={col} style={{ padding: '4px 8px', textAlign: 'center' }}>
                            <DefaultCell
                              value={row[col]}
                              isEditing={isEditing}
                              isSaving={saveMutation.isPending}
                              onStartEdit={() => setEditing(id)}
                              onCommit={(days) => handleCommit(id, days)}
                              onCancel={() => setEditing(null)}
                            />
                          </td>
                        )
                      })}
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        <p style={{ margin: 0, fontSize: 11.5, color: 'var(--text-3)', lineHeight: 1.5 }}>
          Employees with a per-person adjustment (set via the per-employee Adjust button)
          are skipped so their individual entitlement isn&apos;t overwritten.
        </p>
      </div>
    </Modal>
  )
}

// ── Editable cell ──────────────────────────────────────────────────────────

interface DefaultCellProps {
  value: number
  isEditing: boolean
  isSaving: boolean
  onStartEdit: () => void
  onCommit: (days: number) => void
  onCancel: () => void
}

function DefaultCell({
  value,
  isEditing,
  isSaving,
  onStartEdit,
  onCommit,
  onCancel,
}: DefaultCellProps): JSX.Element {
  const [draft, setDraft] = useState<string>(String(value))

  if (isEditing) {
    return (
      <input
        type="number"
        min={0}
        step={1}
        autoFocus
        value={draft}
        disabled={isSaving}
        data-testid="defaults-cell-input"
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            const parsed = parseInt(draft, 10)
            if (!isNaN(parsed) && parsed >= 0) onCommit(parsed)
          } else if (e.key === 'Escape') {
            onCancel()
          }
        }}
        onBlur={() => {
          const parsed = parseInt(draft, 10)
          if (!isNaN(parsed) && parsed >= 0) onCommit(parsed)
          else onCancel()
        }}
        style={{
          width: 64,
          padding: '4px 8px',
          borderRadius: 6,
          border: '1.5px solid var(--steel-blue)',
          background: 'var(--surface)',
          color: 'var(--text)',
          fontSize: 14,
          fontFamily: 'var(--font-mono, monospace)',
          textAlign: 'center',
          outline: 'none',
        }}
      />
    )
  }

  return (
    <button
      type="button"
      onClick={onStartEdit}
      aria-label={`Edit — currently ${value} days`}
      style={{
        width: 64,
        padding: '4px 8px',
        borderRadius: 6,
        border: '1px solid transparent',
        background: 'transparent',
        color: 'var(--text)',
        fontSize: 14,
        fontFamily: 'var(--font-mono, monospace)',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'border-color 80ms, background 80ms',
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.borderColor = 'var(--border)'
        e.currentTarget.style.background = 'var(--surface-2)'
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.borderColor = 'transparent'
        e.currentTarget.style.background = 'transparent'
      }}
    >
      {value}
    </button>
  )
}
